import { supabase } from '../lib/supabase';
import type { Expense, PayrollEntry, PayrollRun } from '../types';
import { recalcularEntrada, rotuloMes, type EntradaFolha } from '../../supabase/functions/_shared/payroll';
import { hojeLocal, mesDe } from '../../supabase/functions/_shared/recebiveis';
import { resumoDaFolha } from '../lib/payrollReport';

// Camada de serviço da folha de pagamento. Gerar, aprovar e reabrir passam pela edge function
// payroll-automation (a mesma que o agendamento diário usa); edições de linha do rascunho, vales e
// baixa das contas a pagar são gravações diretas protegidas por RLS (permissão payroll.access).

export interface PayrollMonth {
  run: PayrollRun | null;
  entries: PayrollEntry[];
}

async function edgeError(error: unknown, fallback: string): Promise<Error> {
  const ctx = (error as { context?: { json?: () => Promise<{ error?: string }> } })?.context;
  if (ctx?.json) {
    try {
      const body = await ctx.json();
      if (body?.error) return new Error(body.error);
    } catch { /* usa a mensagem padrão */ }
  }
  return new Error(fallback);
}

export async function fetchPayrollMonth(companyId: number, mes: string): Promise<PayrollMonth> {
  if (!supabase) return { run: null, entries: [] };
  const { data: run, error } = await supabase.from('payroll_runs').select('*')
    .eq('company_id', companyId).eq('period', `${mes}-01`).maybeSingle();
  if (error) throw error;
  if (!run) return { run: null, entries: [] };
  const { data: entries, error: entriesError } = await supabase.from('payroll_entries').select('*')
    .eq('run_id', run.id).order('employee_name');
  if (entriesError) throw entriesError;
  return { run: run as PayrollRun, entries: (entries || []) as PayrollEntry[] };
}

export async function runPayrollAction(action: 'generate' | 'approve' | 'reopen', companyId: number, mes: string): Promise<void> {
  if (!supabase) throw new Error('Supabase não configurado.');
  const { error } = await supabase.functions.invoke('payroll-automation', {
    body: { action, company_id: companyId, period: mes },
  });
  if (error) {
    const fallback = action === 'generate' ? 'Não foi possível gerar a folha.'
      : action === 'approve' ? 'Não foi possível aprovar a folha.'
        : 'Não foi possível reabrir a folha.';
    throw await edgeError(error, fallback);
  }
}

const toEngine = (e: PayrollEntry): EntradaFolha => ({
  employeeId: e.employee_id || '',
  nome: e.employee_name,
  fixo: Number(e.fixed_amount),
  comissaoBase: Number(e.commission_base),
  comissaoPercent: Number(e.commission_percent),
  comissaoValor: Number(e.commission_amount),
  bonus: Number(e.bonus_amount),
  // advances_amount = vale DESCONTADO nesta folha; o tomado no mês fica em details (folhas antigas: igual ao descontado)
  valeTomado: Number(e.details?.valeTomado ?? e.advances_amount),
  vales: Number(e.advances_amount),
  descontoManual: e.details?.descontoValeManual ?? null,
  pago: Number(e.paid_amount),
  saldoAnterior: Number(e.carry_in),
  saldoVale: Number(e.details?.saldoVale ?? Math.max(-Number(e.total_due), 0)),
  total: Number(e.total_due),
  orion: Number(e.orion_amount) || 0,
  notas: e.notes ?? null,
  detalhes: {
    recebimentos: e.details?.recebimentos ?? 0,
    brutoRecebido: e.details?.brutoRecebido ?? 0,
    liquidoRecebido: e.details?.liquidoRecebido ?? 0,
    baseManual: !!e.base_manual,
    semUsuario: !!e.details?.semUsuario,
  },
});

export interface EntryEdit {
  bonus: number;
  paid: number;
  notes: string;
  /** Base de comissão digitada à mão; null = usar a calculada pelos recebimentos */
  manualBase: number | null;
  /** Parte do custo da pessoa que é do Orion CRM */
  orion: number;
  /** Vale a descontar nesta folha, definido à mão; null/ausente = automático (o que cabe no apurado) */
  descontoVale?: number | null;
}

/** Aplica a edição e devolve os campos da linha já recalculados (comissão e total). */
export function applyEntryEdit(entry: PayrollEntry, edit: EntryEdit) {
  const calculada = Number(entry.details?.baseCalculada ?? entry.details?.liquidoRecebido ?? 0);
  const recalculada = recalcularEntrada({
    ...toEngine(entry),
    bonus: edit.bonus,
    pago: edit.paid,
    comissaoBase: edit.manualBase ?? calculada,
    orion: edit.orion,
    descontoManual: edit.descontoVale === undefined ? toEngine(entry).descontoManual : edit.descontoVale,
  });
  return {
    bonus_amount: recalculada.bonus,
    advances_amount: recalculada.vales,
    paid_amount: recalculada.pago,
    commission_base: recalculada.comissaoBase,
    commission_amount: recalculada.comissaoValor,
    total_due: recalculada.total,
    orion_amount: recalculada.orion,
    base_manual: edit.manualBase != null,
    details: { ...(entry.details || {}), valeTomado: recalculada.valeTomado, saldoVale: recalculada.saldoVale, descontoValeManual: recalculada.descontoManual ?? null },
    notes: edit.notes.trim() || null,
    updated_at: new Date().toISOString(),
  };
}

export async function saveEntryEdit(entry: PayrollEntry, edit: EntryEdit): Promise<void> {
  if (!supabase) throw new Error('Supabase não configurado.');
  const { error } = await supabase.from('payroll_entries').update(applyEntryEdit(entry, edit)).eq('id', entry.id);
  if (error) throw error;
}

export async function updatePayDate(runId: string, payDate: string): Promise<void> {
  if (!supabase) throw new Error('Supabase não configurado.');
  const { error } = await supabase.from('payroll_runs').update({ pay_date: payDate }).eq('id', runId);
  if (error) throw error;
}

// ---------- Contas a pagar geradas pela folha ----------

export async function fetchPayrollExpenses(expenseIds: number[]): Promise<Expense[]> {
  if (!supabase || expenseIds.length === 0) return [];
  const { data, error } = await supabase.from('expenses').select('*').in('id', expenseIds);
  if (error) throw error;
  return (data || []) as Expense[];
}

export async function setExpensePaid(expenseId: number, paid: boolean, paidAt: string = hojeLocal()): Promise<void> {
  if (!supabase) throw new Error('Supabase não configurado.');
  const { error } = await supabase.from('expenses')
    .update({ status: paid ? 'paid' : 'pending', paid_at: paid ? paidAt : null })
    .eq('id', expenseId);
  if (error) throw error;
}

export async function setExpensesPaid(expenseIds: number[], paidAt: string = hojeLocal()): Promise<void> {
  if (!supabase || expenseIds.length === 0) return;
  const { error } = await supabase.from('expenses').update({ status: 'paid', paid_at: paidAt }).in('id', expenseIds);
  if (error) throw error;
}

// ---------- Histórico ----------

export interface PayrollHistoryRow {
  run: PayrollRun;
  mes: string;
  resumo: ReturnType<typeof resumoDaFolha>;
}

/** Folhas do mais recente para o mais antigo, com o que foi pago/pendente de cada uma. */
export async function fetchPayrollHistory(companyId: number, limit = 36): Promise<PayrollHistoryRow[]> {
  if (!supabase) return [];
  const { data: runs, error } = await supabase.from('payroll_runs').select('*')
    .eq('company_id', companyId).order('period', { ascending: false }).limit(limit);
  if (error) throw error;
  const folhas = (runs || []) as PayrollRun[];
  if (folhas.length === 0) return [];

  const { data: entries, error: entriesError } = await supabase.from('payroll_entries').select('*').in('run_id', folhas.map(r => r.id));
  if (entriesError) throw entriesError;
  const todas = (entries || []) as PayrollEntry[];
  const despesaIds = todas.map(e => e.expense_id).filter((id): id is number => id != null);
  const despesas = new Map((await fetchPayrollExpenses(despesaIds)).map(x => [x.id, x]));

  return folhas.map(run => ({
    run,
    mes: run.period.slice(0, 7),
    resumo: resumoDaFolha(run, todas.filter(e => e.run_id === run.id), despesas),
  }));
}

// ---------- Avisos ----------

export interface PayrollAlert {
  /** Chave estável: o aviso só aparece uma vez por dia */
  key: string;
  title: string;
  description: string;
  link: string;
}

const brl = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const dataBr = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;

/**
 * Avisos de folha para quem tem payroll.access:
 *  - rascunho de um mês já fechado aguardando conferência;
 *  - pagamentos da folha vencidos ou que vencem em até 2 dias.
 */
export async function fetchPayrollAlerts(companyId: number, hoje: string = hojeLocal()): Promise<PayrollAlert[]> {
  if (!supabase) return [];
  const alertas: PayrollAlert[] = [];
  const mesAtual = mesDe(hoje);

  const { data: rascunhos } = await supabase.from('payroll_runs').select('period')
    .eq('company_id', companyId).eq('status', 'draft').lt('period', `${mesAtual}-01`)
    .order('period', { ascending: false }).limit(1);
  const rascunho = rascunhos?.[0] as { period: string } | undefined;
  if (rascunho) {
    const mes = rascunho.period.slice(0, 7);
    alertas.push({
      key: `draft:${mes}`,
      title: 'Folha aguardando conferência',
      description: `A folha de ${rotuloMes(mes)} está calculada. Confira os valores e aprove para lançar em Contas a Pagar.`,
      link: `/finance?tab=payroll&month=${mes}`,
    });
  }

  const limite = (() => {
    const d = new Date(`${hoje}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + 2);
    return d.toISOString().slice(0, 10);
  })();
  const { data: pendentes } = await supabase.from('expenses').select('amount, due_date')
    .eq('company_id', companyId).eq('status', 'pending').not('payroll_entry_id', 'is', null)
    .lte('due_date', limite).order('due_date');
  const lista = (pendentes || []) as Array<{ amount: number; due_date: string | null }>;
  if (lista.length > 0) {
    const total = lista.reduce((s, p) => s + Number(p.amount), 0);
    const vencidos = lista.filter(p => p.due_date && p.due_date < hoje).length;
    const primeira = lista[0].due_date || hoje;
    alertas.push({
      key: `pay:${hoje}`,
      title: vencidos > 0 ? 'Pagamentos da folha em atraso' : 'Pagamentos da folha vencendo',
      description: `${lista.length} pagamento(s) somando ${brl(total)}${vencidos > 0 ? `, ${vencidos} já vencido(s)` : `, a partir de ${dataBr(primeira)}`}. Chaves PIX no cadastro de funcionários.`,
      link: '/finance?tab=payroll',
    });
  }
  return alertas;
}

